import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, ReplaySubject, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  SUPPORTED_COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
} from './metadata.models';

const STORAGE_KEY = 'jt_region';
const GEO_TIMEOUT_MS = 4000;

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  private readonly http = inject(HttpClient);

  private readonly country$$ = new BehaviorSubject<string>(DEFAULT_COUNTRY_CODE);
  readonly country$ = this.country$$.asObservable();

  private readonly ready$$ = new ReplaySubject<string>(1);
  readonly ready$ = this.ready$$.asObservable();

  get country(): string {
    return this.country$$.value;
  }

  constructor() {
    this.init();
  }

  setCountry(code: string): void {
    const normalized = code.toUpperCase();
    if (SUPPORTED_COUNTRY_CODES.includes(normalized)) {
      this.country$$.next(normalized);
      localStorage.setItem(STORAGE_KEY, normalized);
    }
  }

  private init(): void {
    const fromQuery = new URLSearchParams(window.location.search)
      .get('region')
      ?.toUpperCase();
    if (fromQuery && SUPPORTED_COUNTRY_CODES.includes(fromQuery)) {
      this.resolve(fromQuery);
      return;
    }

    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage && SUPPORTED_COUNTRY_CODES.includes(fromStorage)) {
      this.resolve(fromStorage);
      return;
    }

    this.http
      .get<{ country: string }>(`${environment.apiUrl}/geo`)
      .pipe(
        timeout(GEO_TIMEOUT_MS),
        map((res) => res.country?.toUpperCase()),
        catchError(() => of(null)),
      )
      .subscribe((code) => {
        this.resolve(
          code && SUPPORTED_COUNTRY_CODES.includes(code)
            ? code
            : DEFAULT_COUNTRY_CODE,
        );
      });
  }

  private resolve(code: string): void {
    this.setCountry(code);
    this.ready$$.next(code);
    this.ready$$.complete();
  }
}
